/**
 * Mobility MVP: Pelvic Tilt Intro Screen
 * Instructions screen before starting the pelvic tilt test
 * Follows FIGMA #MVP-Instruction-Screen layout
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface PelvicTiltIntroProps {
  onStart: () => void;
  onSkip: () => void;
}

export function PelvicTiltIntro({ onStart, onSkip }: PelvicTiltIntroProps) {
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const mountTimeRef = useRef<number>(Date.now());
  const clickCountRef = useRef<number>(0);
  
  // #region agent log
  console.log('[PelvicTiltIntro] Rendering', { 
    onStart: typeof onStart, 
    onSkip: typeof onSkip, 
    hasUserInteracted,
    timeSinceMount: Date.now() - mountTimeRef.current,
    clickCount: clickCountRef.current
  });
  // #endregion
  
  // Prevent auto-click on mount - require at least 500ms and user interaction
  useEffect(() => {
    mountTimeRef.current = Date.now();
    // Set a flag after mount to allow real clicks, but require more time
    const timer = setTimeout(() => {
      setHasUserInteracted(true);
      // #region agent log
      console.log('[PelvicTiltIntro] hasUserInteracted set to true after delay');
      // #endregion
    }, 500); // Increased to 500ms
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <div className="min-h-full w-full bg-background flex items-center justify-center p-4 overflow-auto">
      <Card className="max-w-[420px] w-full my-auto">
        <CardHeader>
          <CardTitle className="text-2xl font-semibold">Pelvic Tilt Control Test</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-base text-foreground">
            This 10-second test helps us understand how well you can control your pelvis — a key part of maintaining posture and rotation during your swing.
          </p>

          <div className="space-y-4">
            <h3 className="font-semibold text-sm">Instructions:</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>
                Set up in golf posture — knees soft, bend from hips, back neutral.
              </li>
              <li>
                Camera at hip height — frame hips + lower back clearly (side view is best).
              </li>
              <li>
                Do 3–4 slow pelvic tilts over 10 seconds:
                <div className="mt-2 ml-5 space-y-1 list-disc text-muted-foreground">
                  <div>- <strong>Tuck/Round</strong>: gently tuck tailbone under (belt buckle up).</div>
                  <div>- <strong>Arch</strong>: gently stick tailbone back (belt buckle down).</div>
                  <div>- Keep chest quiet; movement should come from pelvis, not whole torso.</div>
                </div>
              </li>
            </ol>
          </div>

          <div className="space-y-3 pt-4">
            <Button 
              onClick={(e) => {
                clickCountRef.current += 1;
                const timeSinceMount = Date.now() - mountTimeRef.current;
                // #region agent log
                console.log('[PelvicTiltIntro] Start Test button clicked', { 
                  event: e, 
                  target: e.target, 
                  hasUserInteracted, 
                  isTrusted: e.isTrusted,
                  timeSinceMount,
                  clickCount: clickCountRef.current,
                  detail: e.detail, // Click count (0 = programmatic, 1+ = user)
                  button: e.button, // Mouse button (0 = left, -1 = programmatic)
                });
                // #endregion
                // Prevent auto-clicks: require user interaction flag AND sufficient time AND real click
                if (!hasUserInteracted) {
                  console.warn('[PelvicTiltIntro] Ignoring click - hasUserInteracted is false');
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                if (timeSinceMount < 500) {
                  console.warn('[PelvicTiltIntro] Ignoring click - too soon after mount:', timeSinceMount);
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                if (clickCountRef.current > 1) {
                  console.warn('[PelvicTiltIntro] Ignoring click - already clicked:', clickCountRef.current);
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
                onStart();
              }} 
              className="w-full" 
              size="lg"
              type="button"
              autoFocus={false}
              onMouseDown={(e) => {
                // #region agent log
                console.log('[PelvicTiltIntro] Button mousedown', { isTrusted: e.isTrusted, button: e.button });
                // #endregion
              }}
              onFocus={(e) => {
                // #region agent log
                console.log('[PelvicTiltIntro] Button focused', { target: e.target });
                // #endregion
              }}
            >
              Start Test
            </Button>
            <Button 
              onClick={(e) => {
                // #region agent log
                console.log('[PelvicTiltIntro] Skip button clicked', { event: e, target: e.target });
                // #endregion
                e.preventDefault();
                e.stopPropagation();
                onSkip();
              }} 
              variant="outline" 
              className="w-full" 
              size="lg"
              type="button"
            >
              Skip for now
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
